import * as React from 'react';
/**
 * This is a custom error boundary component that is used to catch errors in federated modules.
 * It is used to display a fallback component when an error occurs or cannot load federated modules.
 * If the module is being lazy loaded, it will display a fallback component until the module is loaded.
 * When importing federated modules on the landing page, this has to be wrapped in a Suspense component or it will keep re-rendering the page.
 */

interface FederatedErrorBoundaryProps {
  children: React.ReactNode;
  fallback: React.ReactNode;
  moduleName: string;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface FederatedErrorBoundaryState {
  hasError: boolean;
}

export class FederatedErrorBoundary extends React.Component<FederatedErrorBoundaryProps, FederatedErrorBoundaryState> {
  constructor(props: FederatedErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): FederatedErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(`Failed to load federated module ${this.props.moduleName}:`, error);
    this.props.onError?.(error, errorInfo);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}
